import { useEffect, useRef } from "react";

import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext";
import { useChatSettingsStore } from "@/features/tutor/store/useChatSettingsStore";
import { useChatTrailStore } from "@/features/tutor/store/useChatTrailStore";
import type { CaptionsToken } from "@/features/tutor/hooks/useCaptionsQueue";
import { isFromOtherTurn } from "@/features/tutor/utils/canvasTurnScope";
import {
  chatTrailTraceArmed,
  installChatTrailTrace,
  trace,
} from "@/features/tutor/utils/chatTrailTrace";

type CaptionsChunk = { chunk_data: string; turn_id: string };

interface HeldToken {
  text: string;
  startMs: number;
  isMath: boolean;
}

export const STALL_GRACE_MS = 1200;

/**
 * How long the clock may sit still, once nothing more is being generated,
 * before the audio counts as dead rather than merely underflowing.
 *
 * Only applied after DONE: before it, tokens are still arriving and a frozen
 * clock is ordinary underflow between TTS chunks.
 */
export const CLOCK_DEAD_MS = 2000;

export const readCaptionToken = (chunk: CaptionsChunk): HeldToken | null => {
  try {
    const token = JSON.parse(chunk.chunk_data) as Partial<CaptionsToken>;
    const text = token.caption_text ?? "";
    if (!text) return null;
    return {
      text,
      isMath: token.is_math === true,
      startMs: token.words?.[0]?.start_ms ?? 0,
      // The backend chunker already decided this; the trail keeps the flag so
      // it does not have to re-derive maths from the string.
    };
  } catch {
    return null;
  }
};

export const useChatTrailSync = () => {
  const { subscribe, pcmPlayerRef } = useRuntimeContext();
  const pendingRef = useRef<HeldToken[]>([]);
  const frameRef = useRef<number | null>(null);
  const stalledSinceRef = useRef<number | null>(null);
  // Whether the audio clock has moved at all for the turn in flight.
  const heardAudioRef = useRef(false);
  const clockRef = useRef<{ ms: number; at: number }>({ ms: -1, at: 0 });
  // DONE means the backend stopped generating, not that the tutor stopped
  // speaking. The turn is only over once the audio has actually finished.
  const generationEndedRef = useRef(false);

  useEffect(() => {
    if (chatTrailTraceArmed()) installChatTrailTrace();

    const trail = () => useChatTrailStore.getState();
    const player = () => pcmPlayerRef?.current ?? window.pcmPlayer;

    const release = (tokens: HeldToken[], reason: string) => {
      if (tokens.length === 0) return;
      trace("release", {
        reason,
        count: tokens.length,
        pending: pendingRef.current.length,
        text: tokens
          .map((t) => t.text)
          .join(" ")
          .slice(0, 40),
      });
      if (trail().pendingIndex === -1) trail().beginTutorTurn();
      tokens.forEach((token) =>
        trail().appendTutorText(token.text, token.isMath),
      );
    };

    const stopTicking = () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      stalledSinceRef.current = null;
    };

    const tick = () => {
      frameRef.current = null;
      if (pendingRef.current.length === 0) return;

      const current = player();
      const playedMs = current?.tickPlayedMs?.() ?? 0;
      if (playedMs > 0) heardAudioRef.current = true;
      if (playedMs !== clockRef.current.ms) {
        clockRef.current = { ms: playedMs, at: Date.now() };
      }

      const due: HeldToken[] = [];
      while (
        pendingRef.current.length > 0 &&
        pendingRef.current[0].startMs <= playedMs
      ) {
        due.push(pendingRef.current.shift() as HeldToken);
      }
      release(due, "clock");

      if (pendingRef.current.length === 0) {
        stalledSinceRef.current = null;
        // Everything generated has now been spoken.
        if (generationEndedRef.current) seal("spoken-out");
        return;
      }

      // The fallback covers a turn with no audio at all — TTS failed, or the
      // pipeline is suppressed. Once the clock has moved, gaps are ordinary
      // underflow between TTS chunks: isPlaying() goes false whenever the
      // scheduled buffers drain, and dumping there flushes the whole reply,
      // because tokens arrive far ahead of the speech.
      // Generation is over and the clock has stopped moving: the audio is not
      // coming back, so the tail would otherwise never be released and this
      // loop would keep scheduling frames forever.
      if (
        generationEndedRef.current &&
        Date.now() - clockRef.current.at >= CLOCK_DEAD_MS
      ) {
        seal("clock-dead");
        return;
      }

      if (heardAudioRef.current || due.length > 0 || current?.isPlaying?.()) {
        stalledSinceRef.current = null;
      } else {
        trace("hold", {
          reason: "no audio yet",
          playedMs,
          playing: current?.isPlaying?.(),
          pending: pendingRef.current.length,
          startMs: pendingRef.current[0]?.startMs,
        });
        stalledSinceRef.current ??= Date.now();
        if (Date.now() - stalledSinceRef.current >= STALL_GRACE_MS) {
          release(pendingRef.current.splice(0), "stall");
          stalledSinceRef.current = null;
          return;
        }
      }

      frameRef.current = requestAnimationFrame(tick);
    };

    const ensureTicking = () => {
      if (frameRef.current === null) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    /** Interrupt or end of speech: nothing more is coming, so flush and close. */
    const seal = (reason: string) => {
      trace("seal", { reason, pending: pendingRef.current.length });
      release(pendingRef.current.splice(0), reason);
      stopTicking();
      heardAudioRef.current = false;
      generationEndedRef.current = false;
      clockRef.current = { ms: -1, at: 0 };
      trail().sealTutorTurn();
    };

    const discard = () => {
      trace("discard", { pending: pendingRef.current.length });
      pendingRef.current = [];
      stopTicking();
      heardAudioRef.current = false;
      generationEndedRef.current = false;
      clockRef.current = { ms: -1, at: 0 };
      trail().sealTutorTurn();
    };

    const onGenerationStart = () => {
      trace("start", { pending: pendingRef.current.length });
      release(pendingRef.current.splice(0), "start");
      heardAudioRef.current = false;
      generationEndedRef.current = false;
    };

    const onGenerationEnd = () => {
      generationEndedRef.current = true;
      trace("hold", {
        reason: "generation done",
        pending: pendingRef.current.length,
        playedMs: player()?.tickPlayedMs?.(),
      });
      if (pendingRef.current.length === 0) seal("done-empty");
    };

    const unsubs = [
      // PRD 8g: the reply is abandoned with the bite it belonged to. Without
      // this the tail of a reply still draining out of pendingRef reopens a
      // turn on the new bite, showing its second half as a fresh message.
      useChatTrailStore.subscribe((state, previous) => {
        if (state.epoch !== previous.epoch) discard();
      }),
      subscribe("CAPTIONS_UPDATE", (chunk: CaptionsChunk) => {
        const token = readCaptionToken(chunk);
        if (!token) return;

        if (!useChatSettingsStore.getState().effectiveTutorAudioOn) {
          release([...pendingRef.current.splice(0), token], "muted");
          return;
        }

        pendingRef.current.push(token);
        trace("arrive", {
          text: token.text.slice(0, 24),
          startMs: token.startMs,
          pending: pendingRef.current.length,
          playedMs: player()?.tickPlayedMs?.(),
          playing: player()?.isPlaying?.(),
        });
        ensureTicking();
      }),
      subscribe("START", onGenerationStart),
      // Generation finished; the voice is still mid-sentence. Keep pacing.
      subscribe("DONE", onGenerationEnd),
      subscribe("AUDIO_PLAYBACK_ENDED", () => seal("audio-ended")),
      subscribe("RESET", discard),
      subscribe("TURN_CHANGE", () => seal("turn-change")),
    ];

    const onCanvasTurnDone = (event: Event) => {
      if (isFromOtherTurn(event)) return;
      onGenerationEnd();
    };
    const onCanvasAudioEnded = () => seal("canvas-audio-ended");
    window.addEventListener("canvas:turn-start", onGenerationStart);
    window.addEventListener("canvas:turn-done", onCanvasTurnDone);
    window.addEventListener("canvas:audio-playback-ended", onCanvasAudioEnded);

    return () => {
      stopTicking();
      unsubs.forEach((unsub) => unsub());
      window.removeEventListener("canvas:turn-start", onGenerationStart);
      window.removeEventListener("canvas:turn-done", onCanvasTurnDone);
      window.removeEventListener(
        "canvas:audio-playback-ended",
        onCanvasAudioEnded,
      );
    };
  }, [subscribe, pcmPlayerRef]);
};
